import { useEffect, useState } from "react";
import { Controller, Control } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";

import { Cohort, User } from "@/api/types";
import { apiClient } from "@/api";
import toast from "@/utils/toast";
import CohortTile from "@/components/users/CohortTile";
import UserTile, { getUserDisplayName } from "@/components/users/UserTile";

type AssignmentsSectionProps = {
  control: Control<any>;
  defaultExpanded?: boolean;
  title?: string;
  sectionId?: string;
};

const parseUsersResponse = (payload: unknown): User[] => {
  if (Array.isArray(payload)) {
    return payload as User[];
  }

  const maybeObject = payload as { users?: User[]; results?: User[] };
  if (Array.isArray(maybeObject.users)) {
    return maybeObject.users;
  }
  if (Array.isArray(maybeObject.results)) {
    return maybeObject.results;
  }

  return [];
};

const parseCohortsResponse = (payload: unknown): Cohort[] => {
  if (Array.isArray(payload)) {
    return payload as Cohort[];
  }

  const maybeObject = payload as { cohorts?: Cohort[]; results?: Cohort[] };
  if (Array.isArray(maybeObject.cohorts)) {
    return maybeObject.cohorts;
  }
  if (Array.isArray(maybeObject.results)) {
    return maybeObject.results;
  }

  return [];
};

const toggleId = (values: number[] | undefined, id: number, checked: boolean) => {
  const source = values || [];
  if (checked) {
    return source.includes(id) ? source : [...source, id];
  }
  return source.filter((value) => value !== id);
};

const getNumericCohortId = (cohort: Cohort): number | null => {
  const cohortId = Number(cohort.id);
  if (Number.isNaN(cohortId)) {
    return null;
  }
  return cohortId;
};

export default function AssignmentsSection({
  control,
  defaultExpanded = false,
  title = "User/Cohort Assignments",
  sectionId = "assignments-section",
}: AssignmentsSectionProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [isAssignmentsLoading, setIsAssignmentsLoading] = useState(true);
  const [isAssignmentsExpanded, setIsAssignmentsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    const fetchAssignmentsData = async () => {
      try {
        setIsAssignmentsLoading(true);
        const [usersPayload, cohortsPayload] = await Promise.all([
          apiClient.get<unknown>("/users/"),
          apiClient.get<unknown>("/cohorts/"),
        ]);

        setUsers(parseUsersResponse(usersPayload));
        setCohorts(parseCohortsResponse(cohortsPayload));
      } catch (error) {
        console.error("Error loading users/cohorts:", error);
        toast.apiError("Failed to load users and cohorts", error);
      } finally {
        setIsAssignmentsLoading(false);
      }
    };

    fetchAssignmentsData();
  }, []);

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => setIsAssignmentsExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-md px-1 py-1 text-left hover:bg-gray-200/50"
        aria-expanded={isAssignmentsExpanded}
        aria-controls={sectionId}
      >
        <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">{title}</h4>
        <FontAwesomeIcon
          icon={isAssignmentsExpanded ? faChevronDown : faChevronRight}
          className="text-gray-500"
        />
      </button>

      {isAssignmentsExpanded && (
        <div id={sectionId}>
          {isAssignmentsLoading ? (
            <p className="text-sm text-gray-500">Loading users and cohorts...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 rounded-md p-3">
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Participants (Users)</h5>
                {users.length === 0 ? (
                  <p className="text-xs text-gray-500">No users available.</p>
                ) : (
                  <Controller
                    control={control}
                    name="participants"
                    render={({ field: { value, onChange } }) => (
                      <div className="grid grid-cols-1 gap-2 max-h-72 overflow-auto pr-1">
                        {users.map((user) => (
                          <UserTile
                            key={`participant-user-${user.id}`}
                            user={user}
                            checkbox={{
                              checked: (value || []).includes(user.id),
                              onChange: (checked) => onChange(toggleId(value, user.id, checked)),
                              label: `Toggle participant ${getUserDisplayName(user)}`,
                            }}
                            className="bg-white"
                          />
                        ))}
                      </div>
                    )}
                  />
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded-md p-3">
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Participant Cohorts</h5>
                {cohorts.length === 0 ? (
                  <p className="text-xs text-gray-500">No cohorts available.</p>
                ) : (
                  <Controller
                    control={control}
                    name="participantCohorts"
                    render={({ field: { value, onChange } }) => (
                      <div className="grid grid-cols-1 gap-2 max-h-72 overflow-auto pr-1">
                        {cohorts.map((cohort) => {
                          const cohortId = getNumericCohortId(cohort);
                          if (cohortId === null) {
                            return null;
                          }
                          return (
                            <CohortTile
                              key={`participant-cohort-${cohort.id}`}
                              cohort={cohort}
                              checkbox={{
                                checked: (value || []).includes(cohortId),
                                onChange: (checked) => onChange(toggleId(value, cohortId, checked)),
                                label: `Toggle participant cohort ${cohort.name}`,
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                  />
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded-md p-3">
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Facilitators (Users)</h5>
                {users.length === 0 ? (
                  <p className="text-xs text-gray-500">No users available.</p>
                ) : (
                  <Controller
                    control={control}
                    name="facilitators"
                    render={({ field: { value, onChange } }) => (
                      <div className="grid grid-cols-1 gap-2 max-h-72 overflow-auto pr-1">
                        {users.map((user) => (
                          <UserTile
                            key={`facilitator-user-${user.id}`}
                            user={user}
                            checkbox={{
                              checked: (value || []).includes(user.id),
                              onChange: (checked) => onChange(toggleId(value, user.id, checked)),
                              label: `Toggle facilitator ${getUserDisplayName(user)}`,
                            }}
                            className="bg-white"
                          />
                        ))}
                      </div>
                    )}
                  />
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded-md p-3">
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Facilitator Cohorts</h5>
                {cohorts.length === 0 ? (
                  <p className="text-xs text-gray-500">No cohorts available.</p>
                ) : (
                  <Controller
                    control={control}
                    name="facilitatorCohorts"
                    render={({ field: { value, onChange } }) => (
                      <div className="grid grid-cols-1 gap-2 max-h-72 overflow-auto pr-1">
                        {cohorts.map((cohort) => {
                          const cohortId = getNumericCohortId(cohort);
                          if (cohortId === null) {
                            return null;
                          }
                          return (
                            <CohortTile
                              key={`facilitator-cohort-${cohort.id}`}
                              cohort={cohort}
                              checkbox={{
                                checked: (value || []).includes(cohortId),
                                onChange: (checked) => onChange(toggleId(value, cohortId, checked)),
                                label: `Toggle facilitator cohort ${cohort.name}`,
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}